/**
 * @file contentService.ts
 * @description Service for handling content-related API calls.
 * Provides functionality for downloading, exporting and managing content.
 */

import config from "../config/config";
import { auth } from "../config/firebaseConfig";
import log from "../config/logging";
import {
  RefineContentPayload,
  RefineContentApiResponse,
} from "../types";

/**
 * Sanitizes a filename by removing invalid characters
 * @param filename - Original filename
 * @returns Sanitized filename
 */
const sanitizeFilename = (filename: string): string => {
  return filename.replace(/[^a-z0-9]/gi, "_");
};

/**
 * Creates and triggers a download for a file
 * @param blob - File content as Blob
 * @param filename - Name for the downloaded file
 */
const downloadFile = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a); // Cleanup
};

export const downloadContent = async (
  contentId: string,
  title: string,
  format: "docx" | "wxr" = "docx"
): Promise<void> => {
  try {
    const token = await auth.currentUser?.getIdToken();
    const response = await fetch(
      `${config.backendUrl}/api/contents/${contentId}/download/${format}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`);
    }

    const blob = await response.blob();
    const sanitizedTitle = sanitizeFilename(title);
    downloadFile(blob, `${sanitizedTitle}.${format}`);

    await updateContentDownloadedStatus(contentId);
  } catch (error) {
    log.error(`${format.toUpperCase()} download error`, { error, contentId });
    throw error;
  }
};

export const refineContent = async (
  contentDetailId: string,
  payload: RefineContentPayload
): Promise<RefineContentApiResponse> => {
  const token = await auth.currentUser?.getIdToken();

  if (!token) {
    throw new Error("Authentication required");
  }

  log.debug("Calling refinement endpoint", {
    contentDetailId,
    includeOriginalInputs: payload.includeOriginalInputs,
    includeInternalLinks: payload.includeInternalLinks,
  });

  const response = await fetch(
    `${config.backendUrl}/api/contents/${contentDetailId}/refine`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    log.error("Refinement request failed", {
      status: response.status,
      statusText: response.statusText,
      errorBody,
    });
    throw new Error(errorBody.message || "Failed to refine content");
  }

  const data: RefineContentApiResponse = await response.json();
  return data;
};

export const downloadPlanContents = async (
  planId: string,
  planName: string,
  format: "docx" | "wxr" = "docx"
): Promise<void> => {
  try {
    const token = await auth.currentUser?.getIdToken();
    log.info(`Downloading plan contents for planId: ${planId}`);

    const response = await fetch(
      `${config.backendUrl}/api/contents/plan/${planId}/download/${format}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`);
    }

    const blob = await response.blob();
    const sanitizedName = sanitizeFilename(planName);
    downloadFile(blob, `${sanitizedName}.${format}`);

    // Fetch plan details to get content IDs
    const planResponse = await fetch(
      `${config.backendUrl}/api/plans/${planId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!planResponse.ok) {
      throw new Error("Failed to fetch plan details");
    }

    const planData = await planResponse.json();

    // Update downloaded status for each content
    if (planData.plan && Array.isArray(planData.plan.contents)) {
      await Promise.all(
        planData.plan.contents.map(async (content: { id: string }) => {
          try {
            await updateContentDownloadedStatus(content.id);
          } catch (error) {
            log.error(
              `Failed to update downloaded status for content: ${content.id}`,
              error
            );
          }
        })
      );
    }

    log.info(`Plan contents downloaded successfully as ${format}`);
  } catch (error) {
    log.error(`Plan ${format.toUpperCase()} download error`, { error, planId });
    throw error;
  }
};

export const updateContentDownloadedStatus = async (
  contentDetailId: string
): Promise<void> => {
  const token = await auth.currentUser?.getIdToken();
  log.debug(
    `Updating downloaded status for contentDetailId: ${contentDetailId}`
  );

  const response = await fetch(
    `${config.backendUrl}/api/contents/${contentDetailId}/downloaded`,
    {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to update downloaded status");
  }

  log.debug(
    `Successfully updated downloaded status for contentDetailId: ${contentDetailId}`
  );
};

export const downloadContentAsWXR = async (
  contentId: string,
  title: string
): Promise<void> => {
  try {
    log.debug(`Starting WXR export for contentId: ${contentId}`);
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error("Authentication required");
    }

    const response = await fetch(
      `${config.backendUrl}/api/contents/${contentId}/export/wxr`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      log.error("WXR export failed", { status: response.status, errorData });
      throw new Error(errorData.message || "Export failed");
    }

    const blob = await response.blob();
    const sanitizedTitle = sanitizeFilename(title);
    downloadFile(blob, `${sanitizedTitle}.wxr`);
    log.info(`WXR export completed successfully`);
  } catch (error) {
    log.error("WXR export error", { error });
    throw error;
  }
};
